// VAST Ads Mixin for media-chrome <hls-video>
// Adds preroll VAST support using @dailymotion/vast-client

import { VASTClient, VASTTracker } from '@dailymotion/vast-client';

/**
 * Mixin that enhances a video element with VAST preroll ad support.
 * Usage:
 *   class HlsVideoWithAds extends VASTAdsMixin(HlsVideo) {}
 *   customElements.define('hls-video-ads', HlsVideoWithAds);
 *
 * Attributes:
 *   vast-src: URL to VAST XML
 *   ad-timeout: optional timeout (ms) before failing ad and playing content
 *   skip-delay: optional time (ms) before skip button appears (default: 5000)
 */
export function VASTAdsMixin(Base) {
  return class VASTAds extends Base {
    static get observedAttributes() {
      return (super.observedAttributes || []).concat([
        'vast-src',
        'ad-timeout',
        'skip-delay',
      ]);
    }

    constructor() {
      super();

      if (!this.hasAttribute('vast-src')) {
        return;
      }

      // Core ad state
      this._adsClient = new VASTClient();
      this._adsPlayed = false;
      this._originalSrc = null;
      this._timeoutId = null;
      this._skipDelay = 5000;
      this._quartilesFired = {};
      this._googleWhyThisAdIcon = null;
      this._onGoogleWhyThisAdClick = this._onGoogleWhyThisAdClick?.bind(this) ||
        ((_e) => {});

      // Bind ad event handlers as class properties for reliable add/remove
      this._onPlay = this._handlePlay.bind(this);
      this._onAdCanPlay = this._onAdCanPlay.bind(this);
      this._onAdEnded = this._onAdEnded.bind(this);
      this._onAdError = this._onAdError.bind(this);
      this._onAdPlaying = this._onAdPlaying.bind(this);
      this._onAdLoadedMetadata = this._onAdLoadedMetadata.bind(this);
      this._onAdProgress = this._onAdProgress.bind(this);
      this._onAdVolumeChange = this._onAdVolumeChange.bind(this);
      this._onAdPause = this._onAdPause.bind(this);
      this._onAdPlay = this._onAdPlay.bind(this);
      this._onAdFullscreenChange = this._onAdFullscreenChange.bind(this);
      this._onAdClick = this._onAdClick.bind(this);
      this._createAdOverlay();
    }

    connectedCallback() {
      super.connectedCallback?.();
      this.addEventListener('play', this._onPlay, { once: true });
    }

    attributeChangedCallback(name, oldVal, newVal) {
      if (name === 'vast-src') this._vastSrc = newVal;
      if (name === 'ad-timeout') this._adTimeout = parseInt(newVal, 10) || 5000;
      if (name === 'skip-delay') this._skipDelay = parseInt(newVal, 10) || 5000;
      super.attributeChangedCallback?.(name, oldVal, newVal);
    }

    // --- Overlay and UI ---
    _createAdOverlay() {
      this._adOverlay = document.createElement('div');
      this._adOverlay.id = 'vast-overlay';
      this._adOverlay.style.cssText = `
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: rgba(0, 0, 0, 0.8);
                display: none;
                z-index: 1000;
                pointer-events: none;
            `;

      // Ad title display
      this._adTitleDisplay = document.createElement('div');
      this._adTitleDisplay.id = 'vast-ad-title';
      this._adTitleDisplay.style.cssText = `
                position: absolute;
                top: 20px;
                left: 20px;
                right: 20px;
                color: white;
                font-size: 20px;
                font-weight: bold;
                z-index: 1002;
                text-shadow: 0 2px 8px rgba(0,0,0,0.7);
                pointer-events: none;
                display: none;
                overflow: hidden;
                text-overflow: ellipsis;
                min-height: 24px;
                line-height: 1.2;
                padding-bottom: 2px;
            `;

      // Ad description display
      this._adDescriptionDisplay = document.createElement('div');
      this._adDescriptionDisplay.id = 'vast-ad-description';
      this._adDescriptionDisplay.style.cssText = `
                position: absolute;
                top: 48px;
                left: 20px;
                right: 20px;
                color: #e0e0e0;
                font-size: 15px;
                z-index: 1002;
                text-shadow: 0 1px 4px rgba(0,0,0,0.7);
                pointer-events: none;
                display: none;
                overflow: hidden;
                text-overflow: ellipsis;
                min-height: 18px;
                line-height: 1.2;
                padding-bottom: 2px;
            `;

      // Create separate video element for ad playback
      this._adVideo = document.createElement('video');
      this._adVideo.id = 'vast-ad-video';
      this._adVideo.style.cssText = `
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                object-fit: contain;
                z-index: 1000;
            `;
      this._adVideo.controls = false;
      this._adVideo.muted = false;
      this._adVideo.playsInline = true;

      this._progressBar = document.createElement('div');
      this._progressBar.id = 'vast-ad-progress-bar';
      this._progressBar.style.cssText = `
                position: absolute;
                bottom: 0;
                left: 0;
                height: 4px;
                background: #ff0000;
                width: 0%;
                transition: width 0.5s linear;
                z-index: 1001;
            `;

      // Add ad time display
      this._adTimeDisplay = document.createElement('div');
      this._adTimeDisplay.id = 'vast-ad-time-display';
      this._adTimeDisplay.style.cssText = `
                position: absolute;
                bottom: 20px;
                left: 20px;
                padding: 4px 10px;
                background: rgba(0, 0, 0, 0.7);
                color: white;
                border-radius: 4px;
                font-size: 14px;
                z-index: 1001;
                pointer-events: none;
                min-width: 60px;
                text-align: center;
                display: none;
            `;

      this._skipButton = document.createElement('button');
      this._skipButton.id = 'vast-ad-skip-button';
      this._skipButton.style.cssText = `
                position: absolute;
                bottom: 50px;
                right: 20px;
                padding: 8px 16px;
                background: rgba(0, 0, 0, 0.7);
                color: white;
                border: none;
                border-radius: 4px;
                cursor: pointer;
                display: none;
                z-index: 1001;
                pointer-events: auto;
            `;
      this._skipButton.textContent = 'Skip Ad';
      this._skipButton.addEventListener('click', () => this._skipAd());

      this._adOverlay.appendChild(this._adVideo);
      this._adOverlay.appendChild(this._progressBar);
      this._adOverlay.appendChild(this._adTimeDisplay);
      this._adOverlay.appendChild(this._adTitleDisplay);
      this._adOverlay.appendChild(this._adDescriptionDisplay);
      this._adOverlay.appendChild(this._skipButton);

      // Insert the overlay after the video element to ensure proper layering
      this.appendChild(this._adOverlay);
    }

    _showAdOverlay() {
      this._adOverlay.style.display = 'block';
      this._adOverlay.style.pointerEvents = 'auto';
      this._skipButton.style.display = 'none';
      this._progressBar.style.width = '0%';
      this._adTimeDisplay.style.display = 'block';
      this._adTimeDisplay.textContent = '';
      this._adTitleDisplay.style.display = this._adTitleDisplay.textContent
        ? 'block'
        : 'none';
      this._adDescriptionDisplay.style.display =
        this._adDescriptionDisplay.textContent ? 'block' : 'none';
      this._adVideo.style.display = 'block';

      // GoogleWhyThisAd icon rendering
      if (this._googleWhyThisAdIconData && !this._googleWhyThisAdIcon) {
        this._googleWhyThisAdIcon = document.createElement('img');
        this._googleWhyThisAdIcon.src = this._googleWhyThisAdIconData.image;
        this._googleWhyThisAdIcon.alt = 'Why this ad?';
        this._googleWhyThisAdIcon.style.cssText = `
                    position: absolute;
                    ${this._googleWhyThisAdIconData.yPosition}: 20px;
                    ${this._googleWhyThisAdIconData.xPosition}: 20px;
                    width: ${this._googleWhyThisAdIconData.width}px;
                    height: ${this._googleWhyThisAdIconData.height}px;
                    z-index: 1003;
                    cursor: pointer;
                    background: transparent;
                `;
        this._googleWhyThisAdIcon.addEventListener(
          'click',
          this._onGoogleWhyThisAdClick,
        );
        this._adOverlay.appendChild(this._googleWhyThisAdIcon);
      }
    }

    _hideAdOverlay() {
      this._adOverlay.style.display = 'none';
      this._adOverlay.style.pointerEvents = 'none';
      this._adVideo.style.display = 'none';
      this._adTimeDisplay.style.display = 'none';
      this._adTimeDisplay.textContent = '';
      this._adTitleDisplay.style.display = 'none';
      this._adTitleDisplay.textContent = '';
      this._adDescriptionDisplay.style.display = 'none';
      this._adDescriptionDisplay.textContent = '';
      this.style.display = 'block';
      this.style.visibility = 'visible';
      if (this._googleWhyThisAdIcon) {
        this._googleWhyThisAdIcon.removeEventListener(
          'click',
          this._onGoogleWhyThisAdClick,
        );
        this._googleWhyThisAdIcon.remove();
        this._googleWhyThisAdIcon = null;
      }
    }

    _updateProgress() {
      if (!this._adVideo || !this._adVideo.duration) return;
      const progress = (this._adVideo.currentTime / this._adVideo.duration) *
        100;
      this._progressBar.style.width = `${progress}%`;
      this._adTimeDisplay.textContent = `${
        this._formatTime(this._adVideo.currentTime)
      } / ${this._formatTime(this._adVideo.duration)}`;
    }

    _formatTime(time) {
      if (!isFinite(time)) return '0:00';
      const minutes = Math.floor(time / 60);
      const seconds = Math.floor(time % 60).toString().padStart(2, '0');
      return `${minutes}:${seconds}`;
    }

    // --- Ad Playback Logic ---
    _skipAd() {
      this._adTracker?.track('skip');
      this._cleanupAd();
    }

    async _handlePlay(event) {
      if (this._adsPlayed || !this._vastSrc) {
        return;
      }

      event.preventDefault();
      this.pause();
      this._originalSrc = this.src;
      this._originalCurrentTime = this.currentTime || 0;
      this.dispatchEvent(new CustomEvent('adsstart'));
      try {
        const mediaUrl = await this._fetchAdMedia();
        if (!mediaUrl) {
          console.info('VAST: no playable ad, skipping preroll');
          this._adsPlayed = true;
          return;
        }
        await this._playAd(mediaUrl);
      } catch (err) {
        console.warn('VAST Ads failed:', err);
      } finally {
        this._cleanupAd();
      }
    }

    _fetchAdMedia() {
      const timeout = this._adTimeout || 5000;
      const fetchPromise = (async () => {
        console.debug('Fetching VAST from:', this._vastSrc);
        const parsed = await this._adsClient.get(this._vastSrc);
        console.debug('VAST parsed:', parsed);
        if (!parsed.ads || parsed.ads.length === 0) {
          console.info('VAST: no ads found in response');
          return null;
        }
        const ad = parsed.ads[0];
        if (!ad) {
          console.info('VAST: missing ad payload');
          return null;
        }
        this._adTitleDisplay.textContent = ad.title || '';
        this._adDescriptionDisplay.textContent = ad.description || '';
        const creative = ad.creatives?.find((c) => c.type === 'linear');
        if (!creative) {
          console.warn('VAST: no linear creative found, skipping');
          return null;
        }
        if (creative.skipDelay) {
          this._skipDelay = creative.skipDelay * 1000;
        }
        this._adClickThroughUrl = null;
        const clickThrough = creative.videoClickThroughURLTemplate ||
          creative.videoClicks?.clickThrough ||
          creative.videoClicks?.ClickThrough;
        if (clickThrough) {
          this._adClickThroughUrl = typeof clickThrough === 'string'
            ? { url: clickThrough }
            : clickThrough;
        }
        const mediaFile = creative.mediaFiles?.find((f) =>
          f.mimeType?.includes('mp4')
        );
        if (!mediaFile) {
          console.warn('VAST: no suitable media file found, skipping');
          return null;
        }
        this._adTracker = new VASTTracker(this._adsClient, ad, creative);
        this._adTracker.trackImpression();
        this._quartilesFired = {
          start: false,
          firstQuartile: false,
          midpoint: false,
          thirdQuartile: false,
        };
        this._googleWhyThisAdIconData = null;
        if (creative.icons && Array.isArray(creative.icons)) {
          const gIcon = creative.icons.find((icon) =>
            icon.program === 'GoogleWhyThisAd'
          );
          if (gIcon) {
            this._googleWhyThisAdIconData = {
              image: gIcon.staticResource ||
                (gIcon.staticResources && gIcon.staticResources[0]),
              clickThrough: gIcon.iconClickThroughURLTemplate ||
                gIcon.iconClickThrough ||
                (gIcon.iconClicks && gIcon.iconClicks.iconClickThrough),
              width: gIcon.width || 18,
              height: gIcon.height || 18,
              xPosition: gIcon.xPosition || 'right',
              yPosition: gIcon.yPosition || 'bottom',
            };
          }
        }
        return mediaFile.fileURL;
      })();
      return Promise.race([
        fetchPromise,
        new Promise((_, reject) => {
          this._timeoutId = setTimeout(
            () => reject(new Error('Ad request timeout')),
            timeout,
          );
        }),
      ]).finally(() => clearTimeout(this._timeoutId));
    }

    _playAd(src) {
      return new Promise((resolve, reject) => {
        this._showAdOverlay();
        // Remove all previous ad event listeners
        this._adVideo.removeEventListener('canplaythrough', this._onAdCanPlay);
        this._adVideo.removeEventListener('ended', this._onAdEnded);
        this._adVideo.removeEventListener('error', this._onAdError);
        this._adVideo.removeEventListener('playing', this._onAdPlaying);
        this._adVideo.removeEventListener(
          'loadedmetadata',
          this._onAdLoadedMetadata,
        );
        this._adVideo.removeEventListener('timeupdate', this._onAdProgress);
        this._adVideo.removeEventListener('pause', this._onAdPause);
        this._adVideo.removeEventListener(
          'volumechange',
          this._onAdVolumeChange,
        );
        this._adVideo.removeEventListener('play', this._onAdPlay);
        document.removeEventListener(
          'fullscreenchange',
          this._onAdFullscreenChange,
        );
        this._adOverlay.removeEventListener('click', this._onAdClick);

        this._adResolve = resolve;
        this._adReject = reject;
        clearTimeout(this._adLoadTimeout);

        this._adVideo.src = src;
        this._adVideo.load();

        this._adVideo.addEventListener('canplaythrough', this._onAdCanPlay, {
          once: true,
        });
        this._adVideo.addEventListener('ended', this._onAdEnded, {
          once: true,
        });
        this._adVideo.addEventListener('error', this._onAdError, {
          once: true,
        });
        this._adVideo.addEventListener('playing', this._onAdPlaying, {
          once: true,
        });
        this._adVideo.addEventListener(
          'loadedmetadata',
          this._onAdLoadedMetadata,
          { once: true },
        );
        this._adVideo.addEventListener('timeupdate', this._onAdProgress);
        this._adVideo.addEventListener('pause', this._onAdPause);
        this._adVideo.addEventListener('volumechange', this._onAdVolumeChange);
        this._adVideo.addEventListener('play', this._onAdPlay);
        document.addEventListener(
          'fullscreenchange',
          this._onAdFullscreenChange,
        );
        this._adOverlay.addEventListener('click', this._onAdClick);

        this._adLoadTimeout = setTimeout(() => {
          if (
            this._adVideo.readyState === 0 && this._adVideo.networkState <= 1
          ) {
            this._onAdError(new Error('Ad failed to load within timeout'));
          } else if (this._adVideo.readyState >= 1) {
            this._adVideo.play().catch((err) => this._onAdError(err));
          } else {
            setTimeout(() => {
              if (this._adVideo.readyState >= 1) {
                this._adVideo.play().catch((err) => this._onAdError(err));
              } else {
                this._onAdError(new Error('Ad failed to load - final timeout'));
              }
            }, 3000);
          }
        }, this._adTimeout || 10000);
      });
    }

    // --- Ad Video Event Handlers ---
    _onAdCanPlay() {
      clearTimeout(this._adLoadTimeout);
      this._adVideo.play().then(() => {
        setTimeout(() => {
          if (this._adOverlay.style.display === 'block') {
            this._skipButton.style.display = 'block';
          }
        }, this._skipDelay);
      }).catch((err) => {
        this._onAdError(err);
      });
    }

    _onAdEnded() {
      this._adTracker?.track('complete');
      this._adResolve?.();
    }

    _onAdError(error) {
      this._adReject?.(error);
    }

    _onAdPlaying() {
      clearTimeout(this._adLoadTimeout);
    }

    _onAdLoadedMetadata() {
      clearTimeout(this._adLoadTimeout);
    }

    _onAdProgress() {
      this._updateProgress();
      if (this._adVideo.readyState >= 2) {
        clearTimeout(this._adLoadTimeout);
      }
      if (!this._adTracker || !this._adVideo.duration) return;
      const ct = this._adVideo.currentTime;
      const dur = this._adVideo.duration;
      if (!this._quartilesFired.start && ct > 0) {
        this._adTracker.track('start');
        this._quartilesFired.start = true;
      }
      if (!this._quartilesFired.firstQuartile && ct >= dur * 0.25) {
        this._adTracker.track('firstQuartile');
        this._quartilesFired.firstQuartile = true;
      }
      if (!this._quartilesFired.midpoint && ct >= dur * 0.5) {
        this._adTracker.track('midpoint');
        this._quartilesFired.midpoint = true;
      }
      if (!this._quartilesFired.thirdQuartile && ct >= dur * 0.75) {
        this._adTracker.track('thirdQuartile');
        this._quartilesFired.thirdQuartile = true;
      }
    }

    // --- Cleanup and Resume ---
    _cleanupAd() {
      this._adsPlayed = true;
      this._hideAdOverlay();
      this._adTimeDisplay.textContent = '';
      this.dispatchEvent(new CustomEvent('adsend'));
      // Remove ad event listeners
      this._adVideo.removeEventListener('canplaythrough', this._onAdCanPlay);
      this._adVideo.removeEventListener('ended', this._onAdEnded);
      this._adVideo.removeEventListener('error', this._onAdError);
      this._adVideo.removeEventListener('playing', this._onAdPlaying);
      this._adVideo.removeEventListener(
        'loadedmetadata',
        this._onAdLoadedMetadata,
      );
      this._adVideo.removeEventListener('timeupdate', this._onAdProgress);
      this._adVideo.removeEventListener('pause', this._onAdPause);
      this._adVideo.removeEventListener('volumechange', this._onAdVolumeChange);
      this._adVideo.removeEventListener('play', this._onAdPlay);
      document.removeEventListener(
        'fullscreenchange',
        this._onAdFullscreenChange,
      );
      this._adOverlay.removeEventListener('click', this._onAdClick);
      clearTimeout(this._adLoadTimeout);
      this._adVideo.pause();
      this._adVideo.src = '';
      this._adVideo.load();
      this._adVideo.remove();
      // Resume the HLS player
      if (this._originalSrc) {
        this.src = this._originalSrc;
        this.load();
        const onCanPlay = () => {
          this.removeAttribute('poster');
          this.style.display = 'block';
          this.style.visibility = 'visible';
          if (this._originalCurrentTime > 0) {
            this.currentTime = this._originalCurrentTime;
          }
          setTimeout(() => {
            this.play().catch(() => {});
          }, 100);
          this.removeEventListener('canplaythrough', onCanPlay);
          this.removeEventListener('loadeddata', onCanPlay);
          this.removeEventListener('error', onError);
        };
        const onError = (_error) => {
          this.removeEventListener('canplaythrough', onCanPlay);
          this.removeEventListener('loadeddata', onCanPlay);
          this.removeEventListener('error', onError);
        };
        this.addEventListener('canplaythrough', onCanPlay, { once: true });
        this.addEventListener('loadeddata', onCanPlay, { once: true });
        this.addEventListener('error', onError, { once: true });
      } else {
        this.play().catch(() => {});
      }
      if (this._googleWhyThisAdIcon) {
        this._googleWhyThisAdIcon.removeEventListener(
          'click',
          this._onGoogleWhyThisAdClick,
        );
        this._googleWhyThisAdIcon.remove();
        this._googleWhyThisAdIcon = null;
      }
    }

    // --- VAST Event Handlers ---
    _onAdVolumeChange() {
      if (!this._adTracker) return;
      if (this._adVideo.muted || this._adVideo.volume === 0) {
        this._adTracker.track('mute');
      } else {
        this._adTracker.track('unmute');
      }
    }
    _onAdPause() {
      if (!this._adVideo.ended && !this._adVideo.paused) return;
      if (!this._adVideo.ended) {
        this._adVideo.play();
      }
    }
    _onAdPlay() {
      if (!this._adTracker) return;
      this._adTracker.track('resume');
    }
    _onAdFullscreenChange() {
      if (!this._adTracker) return;
      if (
        document.fullscreenElement === this._adVideo ||
        document.fullscreenElement === this._adOverlay
      ) {
        this._adTracker.track('fullscreen');
      } else {
        this._adTracker.track('exitFullscreen');
      }
    }
    _onAdClick(e) {
      if (!this._adClickThroughUrl || !this._adClickThroughUrl.url) return;
      if (
        this._adOverlay.style.display === 'block' &&
        e.target !== this._skipButton
      ) {
        this._adTracker?.track('click');
        globalThis.open(this._adClickThroughUrl.url, '_blank');
      }
    }
    _onGoogleWhyThisAdClick = (e) => {
      e.stopPropagation();
      if (
        this._googleWhyThisAdIconData &&
        this._googleWhyThisAdIconData.clickThrough
      ) {
        globalThis.open(this._googleWhyThisAdIconData.clickThrough, '_blank');
      }
    };
  };
}
